import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Page,
  Banner,
  Badge,
  Card,
  Text,
  Button,
  ButtonGroup,
  Divider,
  Link,
  Autocomplete,
  InlineGrid,
  TextField,
  RadioButton,
  IndexTable,
  useBreakpoints,
  Thumbnail,
  Tooltip,
  Spinner,
  InlineStack,
} from "@shopify/polaris";
import axios from "axios";
import {
  ageOptions,
  conditionOptions,
  genderOptions,
  sizeSystemOptions,
  sizeTypeOptions,
  unitOptions,
} from "../../utils/constants";
import warningIcon from "../../icons/warning.png";
import CustomAttributes from "./CustomAttributes";
import ProductCharacteristics from "./ProductCharacteristics";
import BasicDetails from "./BasicDetails";
import icon from "../../icons/icons8-google-48.png";

import ProductLabels from "./ProductLabels";
import MerchantPromotions from "./MerchantPromotions";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatVariantPrice,
  getVariantIssues,
} from "../../utils/productPricing";
import { getProductImageUrl } from "../../utils/productMedia";

function mapShopifyNodeToDetailProduct(node, extras = {}) {
  if (!node) return extras;

  return {
    ...extras,
    id: node.id || extras.id,
    product: node.title || extras.product || "Product",
    category: node.productType || extras.category || "",
    errors: node.errors || extras.errors || [],
    warnings: node.warnings || extras.warnings || [],
    image: getProductImageUrl(node) || extras.image || "",
    variants: node.variants || extras.variants || { edges: [] },
    description: node.description ?? extras.description ?? "",
    metafields: node.metafields || extras.metafields || [],
    alreadyOnGmc: extras.alreadyOnGmc ?? Boolean(node.alreadyOnGmc),
    gmcPresence: extras.gmcPresence || node.gmcPresence || null,
    feedStatus: extras.feedStatus || node.feedStatus || "not_synced",
  };
}

function getVariantThumbUrl(variantNode = {}, productNode = {}) {
  return (
    variantNode.image?.url ||
    variantNode.image?.src ||
    (typeof variantNode.image === "string" ? variantNode.image : "") ||
    productNode.image ||
    getProductImageUrl(productNode) ||
    ""
  );
}

function ProductDetail() {
  // Checkbox states
  const [data, setData] = useState({
    channelSubmissionControl: [],
    category: "",
    color: "",
    material: "",
    cogs: "",
    size: "",
    pattern: "",
    condition: conditionOptions[0].value,
    gender: genderOptions[0].value,
    ageGroup: ageOptions[0].value,
    sizeSystem: sizeSystemOptions[0].value,
    unitPricingMeasure: "",
    unitPricingUnit: unitOptions[0].value,
    sizeType: sizeTypeOptions[0].value,
    unitPricingBaseMeasure: "",
    unitPricingBaseUnit: unitOptions[0].value,
    adultOrientedChecked: false,
    customLabel0: "",
    customLabel1: "",
    customLabel2: "",
    customLabel3: "",
    customLabel4: "",
    adsLabel: "",
    adsGrouping: "",
    shippingLabel: "",
    returnPolicyLabel: "",
  });

  const deselectedOptions = useMemo(
    () => [
      { value: "rustic", label: "Rustic" },
      { value: "antique", label: "Antique" },
      { value: "vinyl", label: "Vinyl" },
      { value: "vintage", label: "Vintage" },
      { value: "refurbished", label: "Refurbished" },
    ],
    []
  );
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(deselectedOptions);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [currencyCode, setCurrencyCode] = useState("");

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const paramValue = urlParams.get("shop");
  const navigate = useNavigate();

  const [product, setProduct] = useState(() => {
    const initial = location.state?.product;
    if (!initial) return null;
    return { ...initial, vendor: paramValue };
  });

  const [upim, setUpim] = useState("disabled");

  const handleUpimChange = useCallback((_, newValue) => setUpim(newValue), []);

  const handleCustomAttributesChange = useCallback((value, id) => {
    setData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  }, []);

  const handleProductCharacteristicsChange = useCallback((value, id) => {
    setData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  }, []);

  const handleAdultOrientedChange = useCallback((checked, name) => {
    setData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  }, []);

  const handleProductLabelsChange = useCallback((value, id) => {
    setData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  }, []);

  const variantEdges = product?.variants?.edges || [];
  const priceCurrency =
    currencyCode ||
    (typeof variantEdges[0]?.node?.price === "object" &&
      (variantEdges[0].node.price.currencyCode ||
        variantEdges[0].node.price.currency)) ||
    "";

  const rowMarkup = (variantsArray) => {
    const edges = variantsArray?.edges || [];
    return edges.map((edge, index) => {
      const node = edge?.node || edge || {};
      const {
        id,
        title,
        sku,
        barcode,
        price,
        availableForSale,
      } = node;
      const issues = getVariantIssues(node, product || {});
      const thumbUrl = getVariantThumbUrl(node, product || {});
      const statusTooltip =
        issues.length > 0
          ? issues.join("; ")
          : product?.alreadyOnGmc
            ? "Already listed on Google Merchant Center"
            : "Ready for Shopping Channels";

      return (
        <IndexTable.Row id={id || `variant-${index}`} key={`${id}-${index}`}>
          <IndexTable.Cell>
            <Tooltip content={statusTooltip}>
              <div>
                {issues.length > 0 ? (
                  <img
                    src={warningIcon}
                    alt={statusTooltip}
                    style={{ width: "16px", height: "16px" }}
                  />
                ) : (
                  <InlineStack gap="100" blockAlign="center">
                    <img
                      src={icon}
                      alt="Google"
                      style={{ width: "16px", height: "16px" }}
                    />
                  </InlineStack>
                )}
              </div>
            </Tooltip>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Thumbnail source={thumbUrl || undefined} alt={title || ""} />
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Link dataPrimaryLink removeUnderline>
              <Text variant="bodyMd" as="span">
                {title || "Default Title"}
              </Text>
            </Link>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div>{sku || "—"}</div>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div>{barcode || "—"}</div>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div>{formatVariantPrice(price, priceCurrency)}</div>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div>{availableForSale ? "In Stock" : "Out Of Stock"}</div>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div>{id ? String(id).split("/").pop() : "—"}</div>
          </IndexTable.Cell>
          <IndexTable.Cell position="center">
            <div />
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    });
  };

  const headings = [
    {
      title: (
        <div>
          <img
            src={icon}
            alt="Google"
            style={{ width: "16px", height: "16px" }}
          />
        </div>
      ),
    },
    { title: "Variant" },
    { title: "" },
    { title: "SKU" },
    { title: "Barcode/GTIN" },
    {
      title: priceCurrency
        ? `Sale Price / Price (${priceCurrency})`
        : "Sale Price / Price",
    },
    { title: "Availability" },
    { title: "Variant ID" },
    { title: "Quick Links" },
  ];

  const updateText = useCallback(
    (value) => {
      setInputValue(value);

      if (value === "") {
        setOptions(
          categories.map((category) => ({ value: category, label: category }))
        );
        return;
      }

      const filterRegex = new RegExp(value, "i");
      const resultOptions = categories
        .map((category) => ({ value: category, label: category }))
        .filter((option) => option.label.match(filterRegex));
      setOptions(resultOptions);
    },
    [categories]
  );

  const updateSelection = useCallback(
    (selected) => {
      const selectedValue = selected.map((selectedItem) => {
        const matchedOption = options.find((option) => {
          return option.value.match(selectedItem);
        });
        return matchedOption && matchedOption.label;
      });

      setSelectedOptions(selected);
      setInputValue(selectedValue[0] || "");
    },
    [options]
  );

  const fetchStoreProduct = useCallback(async () => {
    if (!paramValue || !product?.id) {
      setProductLoading(false);
      return;
    }

    setProductLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/shopify/store/product`,
        {
          params: { shop: paramValue, productId: product.id },
        },
      );

      if (response.data?.status && response.data?.data?.product) {
        const payload = response.data.data;
        setCurrencyCode(payload.currencyCode || "");
        setProduct((prev) =>
          mapShopifyNodeToDetailProduct(payload.product, {
            ...prev,
            vendor: paramValue,
            alreadyOnGmc:
              payload.alreadyOnGmc ??
              prev?.alreadyOnGmc ??
              Boolean(payload.gmcPresence),
            gmcPresence: payload.gmcPresence || prev?.gmcPresence || null,
            feedStatus: prev?.feedStatus || "not_synced",
          }),
        );
      }
    } catch (error) {
      console.error("Error fetching store product:", error);
    } finally {
      setProductLoading(false);
    }
  }, [paramValue, product?.id]);

  const fetchProductDetail = useCallback(async () => {
    if (!paramValue || !product?.id) return;

    setLoading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/shopify/get_product_detail?shop=${paramValue}&productId=${product.id}`;
      const response = await axios.get(apiUrl);
      const payload = response.data;

      if (payload?.status && payload?.data) {
        const productDetail = { ...payload.data };
        delete productDetail.id;
        setData((prev) => ({
          ...prev,
          ...productDetail,
        }));
        if (productDetail.category) {
          setInputValue(productDetail.category);
          setSelectedOptions([productDetail.category]);
        }
      }
    } catch (error) {
      // Saved Google attributes are optional for products never edited in-app.
      console.log("No saved product detail attributes yet");
    } finally {
      setLoading(false);
    }
  }, [paramValue, product?.id]);

  const fetchProductCategories = useCallback(async (search = "") => {
    if (!paramValue) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/google/get-product-categories`,
        { params: { shop: paramValue, search } },
      );
      if (response.status === 200) {
        setCategories(response.data?.categories || []);
      }
    } catch (error) {
      console.log("fetchProductCategories error:", error);
    }
  }, [paramValue]);

  useEffect(() => {
    fetchStoreProduct();
    fetchProductDetail();
    fetchProductCategories();
  }, [fetchStoreProduct, fetchProductDetail, fetchProductCategories]);

  useEffect(() => {
    setOptions(
      categories.map((category) => ({ value: category, label: category })),
    );
  }, [categories]);

  const handleUpdateProduct = async () => {
    if (!paramValue || !product?.id) {
      console.log("Shop or product not found");
      return;
    }

    const payload = {
      ...data,
      productId: product.id,
      shop: paramValue,
      category: inputValue || data.category,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/shopify/update_product_details`,
        { data: payload },
      );
      if (response.data?.status) {
        navigate(`/settings?shop=${paramValue}`);
      } else {
        alert(response.data?.message || "Product update failed");
      }
    } catch (error) {
      console.log("error", error);
      alert("Product update failed");
    }
  };

  const redirectToProductsPage = () => {
    navigate(`/settings?shop=${paramValue}`);
  };

  const { smDown } = useBreakpoints();

  if (!product?.id) {
    return (
      <Page fullWidth>
        <Card>
          <Banner tone="warning" title="Product not found">
            <p>
              Open a product from the catalog to view its Shopping Channel
              details.
            </p>
          </Banner>
          <div style={{ marginTop: 16 }}>
            <Button onClick={redirectToProductsPage}>Back to products</Button>
          </div>
        </Card>
      </Page>
    );
  }

  const productErrors = [
    ...(product.errors || []),
    ...(!product.description ? ["Missing product description."] : []),
  ].filter(Boolean);
  const uniqueProductErrors = [...new Set(productErrors)];

  return (
    <Page fullWidth>
      <Card>
        {productLoading ? (
          <div style={{ padding: 24 }}>
            <Spinner accessibilityLabel="Loading product" size="small" />
          </div>
        ) : null}
        {product.alreadyOnGmc ? (
          <Banner tone="info" title="Already listed on Google Merchant Center">
            <p>
              This Shopify product matches an existing GMC listing
              {product.gmcPresence?.offerId
                ? ` (offer ID: ${product.gmcPresence.offerId})`
                : ""}
              . Syncing will update that listing instead of creating a
              duplicate.
            </p>
          </Banner>
        ) : null}
        {uniqueProductErrors.length > 0 ? (
          <Banner
            tone="warning"
            title={uniqueProductErrors[0]}
          >
            {uniqueProductErrors.length > 1 ? (
              <ul>
                {uniqueProductErrors.slice(1).map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            ) : null}
          </Banner>
        ) : null}
        <div style={{ padding: "12px 0 0 30px" }}>
          <InlineStack gap="200" blockAlign="center">
            {product.alreadyOnGmc ? (
              <Badge tone="info">Already on GMC</Badge>
            ) : null}
            {product.feedStatus === "submitted" ? (
              <Badge tone="success">Synced by app</Badge>
            ) : null}
            {product.feedStatus === "ineligible" ? (
              <Badge tone="critical">Needs fixes</Badge>
            ) : null}
          </InlineStack>
        </div>
        <BasicDetails product={product} />
        {/* Channel Submission Control */}
        {/* <ChannelSubmissionControl
          data={data}
          handleChannelSubmissionControlChange={
            handleChannelSubmissionControlChange
          }
        /> */}
        {/* Google Product Category */}
        <InlineGrid columns={["oneThird", "twoThirds"]}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: "15px",
              paddingLeft: "30px",
              paddingTop: "20px",
              paddingBottom: "20px",
              width: "25vw",
            }}
          >
            <Text variant="headingLg" fontWeight="semibold">
              Google Product Category
            </Text>
            <Text as="p">
              Use the most specific and closest match category from the list of
              predefined Google product category taxonomy. This helps in the
              calculation of US taxes as well as accurate targeting of Ads
              campaigns. It may also resolve any issues caused due to
              enforcement of category-specific attribute requirements.
            </Text>
          </div>
          <div style={{ paddingTop: "25px" }}>
            <Autocomplete
              options={options}
              selected={selectedOptions}
              onSelect={updateSelection}
              textField={
                <TextField
                  onChange={updateText}
                  label="Category"
                  value={inputValue}
                  // prefix={<Icon source={SearchIcon} tone="base" />}
                  placeholder="Search"
                  autoComplete="off"
                />
              }
            />
            {/* <Select
              label="Category"
              options={options}
              onChange={()=>{}}
              value={inputValue}
              placeholder="Type to search for your Google Product Category"
            /> */}
          </div>
        </InlineGrid>
        <Divider />
        <CustomAttributes
          data={data}
          handleCustomAttributesChange={handleCustomAttributesChange}
          product={product}
        />
        <ProductCharacteristics
          data={data}
          handleProductCharacteristicsChange={
            handleProductCharacteristicsChange
          }
          handleAdultOrientedChange={handleAdultOrientedChange}
        />
        {/* Unique Product Identifiers Management */}
        <InlineGrid columns={["oneThird", "twoThirds"]}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: "15px",
              paddingLeft: "30px",
              paddingTop: "20px",
              paddingBottom: "20px",
              width: "25vw",
            }}
          >
            <Text variant="headingLg" fontWeight="semibold">
              Unique Product Identifiers Management
            </Text>
            <Text as="p">
              These options will help you control submission of product
              identifiers. You can submit all the product identifiers or just
              Brand Name & MPN value or entirely as Custom Product.
            </Text>
          </div>
          <div style={{ paddingTop: "25px", paddingBottom: "20px" }}>
            <RadioButton
              label="Submit Brand Name, MPN (SKU) and GTIN (Barcode)"
              helpText="This option will submit all the required product identifiers in the feed. If any identifier value is missing, it will not be submitted. Use this option when your products are updated with all correct unique product identifiers(UPI) same as other merchants for the same product/variant. UPI defines exactly which product you're selling."
              checked={upim === "disabled"}
              id="disabled"
              name="accounts"
              onChange={handleUpimChange}
            />
            <RadioButton
              label="Submit Brand Name and MPN (SKU)"
              helpText="This option will submit Brand Name and MPN (SKU) values only and will ignore GTIN (Barcode) in the product feed. Use this option when you don't have valid Barcode values. This may limit your product's performance."
              id="optional"
              name="accounts"
              checked={upim === "optional"}
              onChange={handleUpimChange}
            />
            <RadioButton
              label="Submit Product as Custom Product"
              helpText="You can submit products entirely as custom when you are the only advertiser running Ads on these products. This option will not submit any unique product identifiers and will submit identifier_exists attribute with a value as FALSE"
              id="optional"
              name="accounts"
              checked={upim === "optional"}
              onChange={handleUpimChange}
            />
          </div>
        </InlineGrid>
        <Divider />
        {/* Product Labels */}
        <ProductLabels
          data={data}
          handleProductLabelsChange={handleProductLabelsChange}
        />
        <MerchantPromotions data={data} />
        {/* <InlineGrid
        columns={["oneThird", "twoThirds"]}
        > */}
        <div
          style={{
            // display: "flex",
            flexDirection: "column",
            rowGap: "15px",
            paddingLeft: "30px",
            paddingTop: "20px",
            paddingBottom: "20px",
            // width: "25vw",
          }}
        >
          <Text variant="headingLg" fontWeight="semibold">
            Variants
          </Text>
          <Text as="p">
            Each Product Variant is submitted to Shopping Channels as a unique
            product with its own listing.
          </Text>
          <IndexTable
            selectable={false}
            condensed={smDown}
            itemCount={variantEdges.length}
            headings={headings}
            loading={productLoading || loading}
          >
            {rowMarkup(product.variants)}
          </IndexTable>
          {!productLoading && variantEdges.length === 0 ? (
            <Text as="p" tone="subdued">
              No variants found for this product.
            </Text>
          ) : null}
        </div>
        <ButtonGroup>
          <Button onClick={redirectToProductsPage}>Cancel</Button>
          <Button onClick={handleUpdateProduct} variant="primary">
            Update Product
          </Button>
        </ButtonGroup>
        {/* </InlineGrid> */}
        <Divider />
      </Card>
    </Page>
  );
}

export default ProductDetail;
